/**
 * PFAP Hub — Guide Mode (Phase 1) content registry.
 *
 * One entry per supported sidebar/section. Each entry has:
 *   id: stable string id (also used as the dismissal key on the backend)
 *   version: integer; bumped in future phases to force a re-show
 *   title: human-readable page title
 *   roles: per-role content. Keys: Admin, Staff, Player, Parent.
 *     Each role block has:
 *       hidden: true        — do not show this guide for this role at all
 *       explanation: string — 1 short paragraph
 *       used_for: string[]  — 2–4 short bullets
 *       attention: string[] — 2–4 short bullets
 *       role_note: string?  — optional role-specific reminder
 *
 * Display rule: if the user's role block is missing OR `hidden:true`,
 * the GuideOverlay does not render. The page itself still renders; the
 * overlay is purely informational and never blocks the UI.
 *
 * Phase 1 stays deliberately simple — no multi-step tours, no field
 * highlights, no videos, no analytics, no admin reset controls.
 */

const GUIDE_VERSION = 1;

// Compact reusable blurbs to keep file readable.
const ROLE_HIDDEN = { hidden: true };

// Helper to attach the version to every entry. `versionOverride` lets
// us bump a single guide (e.g. focus_hub → v2 after the V2.5 cutover)
// without forcing a re-show on every page in the app.
const g = (id, title, roles, versionOverride) => ({
  id,
  version: versionOverride || GUIDE_VERSION,
  title,
  roles,
});

// Common role-note tail ("Pages that are admin-only / staff-only get
// hidden for non-eligible roles via the `roles` block above.")

export const GUIDE_REGISTRY = [
  g('dashboard', 'Dashboard', {
    Admin: {
      explanation: 'Dashboard gives a quick overview of academy activity, important updates, staff actions, alerts, and player information that needs attention today.',
      used_for: [
        'Seeing academy activity at a glance',
        'Checking important updates and alerts',
        'Reviewing recent staff and player activity',
        'Understanding what needs attention today',
      ],
      attention: [
        'Pending actions',
        'Alerts or important updates',
        'Recent activity',
        'Data that may need follow-up',
      ],
    },
    Staff: {
      explanation: 'Dashboard gives a quick overview of academy activity, updates, and the things that need your attention today.',
      used_for: [
        'Seeing academy activity at a glance',
        'Checking updates and alerts',
        'Reviewing recent activity',
        'Spotting items that need follow-up',
      ],
      attention: [
        'Pending actions assigned to you',
        'New alerts or updates',
        'Recent activity in your modules',
        'Items missing data that you can fix',
      ],
    },
    Player: {
      explanation: 'Dashboard shows key academy information, updates, and a quick view of your progress.',
      used_for: [
        'Seeing recent academy updates',
        'Checking your progress at a glance',
        'Finding important notices',
      ],
      attention: [
        'New messages or notices',
        'Recent training or match updates',
        'Anything the academy asks you to follow up on',
      ],
    },
    Parent: {
      explanation: 'Dashboard gives a simple overview of your child\'s academy updates, progress, and important notices.',
      used_for: [
        'Following recent academy updates',
        'Seeing your child\'s progress at a glance',
        'Finding messages or notices',
      ],
      attention: [
        'New messages from staff',
        'Updates the academy shares with you',
        'Anything that needs your action',
      ],
    },
  }),

  g('work_center', 'Staff Work Center', {
    Admin: {
      explanation: 'Staff Work Center is the academy-wide action board. It is generated live by the Missing Data Intelligence engine — there is no "tasks" table to maintain. Each task disappears automatically the moment the underlying data is filled in (auto-resolve). As Admin, you see every task across every Work Area.',
      used_for: [
        'Reviewing all missing/incomplete records across the academy (PDPs, biometrics, club assignments, season reports, documents, etc.)',
        'Assigning Work Areas to Staff so they see only the tasks relevant to their role',
        'Snoozing low-urgency tasks until a date, or Ignoring tasks that are not applicable',
        'Marking a task as In Progress so the team can see active work',
        'Previewing what a specific Staff user will see by filtering tasks by their Work Areas',
      ],
      attention: [
        'Tasks auto-resolve — there is no Mark Complete; filling the underlying data is what removes the task on the next scan',
        'Snooze / Ignore are stored as overrides; Reset clears them and the task reappears if the data is still missing',
        'Staff with NO Work Areas assigned will see ZERO tasks — assign their areas in User Management → User Details',
        'Each task carries the list of Work Areas it belongs to (e.g. "PDP", "Biometrics") — that is the visibility filter for Staff',
      ],
      role_note: 'Work Areas (Football, PDP, Performance, Biometrics, Nutrition, Mentorship, Education, Career Pathway, Injury Record, Documents, etc.) are set per Staff user from User Management → Open user → Work Areas.',
    },
    Staff: {
      explanation: 'Staff Work Center shows the missing/incomplete data tasks that fall inside YOUR Work Areas. Tasks are generated live by the Missing Data Intelligence engine and auto-resolve the moment the underlying data is filled.',
      used_for: [
        'Seeing only the tasks tied to the Work Areas the Admin has assigned to you',
        'Jumping straight to the data that is missing via the Fix button',
        'Snoozing a task for 7 days when it is not actionable yet',
        'Ignoring a task that does not apply (with a reason)',
        'Marking a task In Progress so the team can see you are on it',
      ],
      attention: [
        'If you see no tasks AT ALL, your account most likely has no Work Areas assigned — ask an Admin',
        'Auto-resolve: once the underlying data exists, the task disappears on the next scan — there is no Complete button',
        'Snooze and Ignore are reversible from the same task card via Reset',
        'Filters let you narrow by status, work area, or priority',
      ],
      role_note: 'Your Work Areas (and therefore the tasks you see) are controlled by an Admin in User Management → User Details → Work Areas.',
    },
  }, 3),

  g('athletes', 'Athletes', {
    Admin: {
      explanation: 'Athletes stores player profiles, team and group details, contact information, development status, and the records that are referenced across the academy.',
      used_for: [
        'Managing athlete profiles',
        'Reviewing team and group information',
        'Connecting player data to training, health, reports, and documents',
        'Keeping academy records accurate',
      ],
      attention: [
        'Missing profile fields',
        'Correct team / group assignment',
        'Correct player status',
        'Up-to-date records',
      ],
    },
    Staff: {
      explanation: 'Athletes is where you find every player\'s profile and the records that connect to training, health, and development work.',
      used_for: [
        'Finding and updating athlete profiles',
        'Reviewing team and group information',
        'Connecting player data to training, health, and reports',
        'Keeping records accurate',
      ],
      attention: [
        'Missing profile fields',
        'Correct group / team assignment',
        'Up-to-date player status',
      ],
    },
    Player: {
      explanation: 'This section shows your athlete profile and the academy-managed information about you.',
      used_for: [
        'Viewing your profile',
        'Checking the information the academy keeps for you',
      ],
      attention: [
        'Anything that looks out of date — let the academy know',
      ],
    },
    Parent: {
      explanation: 'This section shows the athlete information the academy shares about your child.',
      used_for: [
        'Viewing your child\'s profile',
        'Checking the information shared with you',
      ],
      attention: [
        'Anything that looks out of date — let the academy know',
      ],
    },
  }),

  g('athlete_tags', 'Athlete Tags', {
    Admin: {
      explanation: 'Athlete Tags help group players by squad, age group, level, role, or any custom academy category. Tags are used by reports, communication, and permissions.',
      used_for: [
        'Organising athletes into useful groups',
        'Filtering players by team, squad, or category',
        'Supporting reports and communication',
        'Helping staff manage groups efficiently',
      ],
      attention: [
        'Correct tag assignments',
        'Outdated or duplicate tags',
        'Players missing important tags',
        'Groups used by reports or permissions',
      ],
    },
    Staff: {
      explanation: 'Athlete Tags help you group players by squad, age group, level, or role so reports and communication can target the right athletes.',
      used_for: [
        'Grouping athletes',
        'Filtering players for reports and messages',
        'Tagging players for follow-up',
      ],
      attention: [
        'Correct tag assignments',
        'Outdated tags',
        'Players missing important tags',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('team_state', 'Team State', {
    Admin: {
      explanation: 'Team State gives an overview of squad and team condition, availability, and other team-level information.',
      used_for: [
        'Reviewing team readiness',
        'Checking squad availability',
        'Understanding team-level concerns',
        'Supporting staff coordination',
      ],
      attention: [
        'Missing team data',
        'Availability concerns',
        'Team trends',
        'Players needing follow-up',
      ],
    },
    Staff: {
      explanation: 'Team State helps you see how your squad is doing right now — availability, readiness, and team-level concerns.',
      used_for: [
        'Reviewing team readiness',
        'Checking squad availability',
        'Spotting team-level concerns',
      ],
      attention: [
        'Missing team data',
        'Availability concerns',
        'Players needing follow-up',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('media_highlights', 'Media & Highlights', {
    Admin: {
      explanation: 'Media & Highlights stores photos, videos, highlight links, and featured content connected to athletes or academy activity.',
      used_for: [
        'Uploading or reviewing media',
        'Highlighting player moments',
        'Supporting marketing and player profiles',
        'Keeping media organised by athlete or team',
      ],
      attention: [
        'Correct athlete / team tagging',
        'Featured media status',
        'Media quality and relevance',
        'Permission and visibility before sharing externally',
      ],
    },
    Staff: {
      explanation: 'Media & Highlights is where you upload, organise, and share photos, videos, and highlights linked to athletes or academy activity.',
      used_for: [
        'Uploading or reviewing media',
        'Tagging player moments',
        'Organising media by athlete or team',
      ],
      attention: [
        'Correct athlete / team tagging',
        'Featured media status',
        'Permission and visibility before sharing externally',
      ],
    },
    Player: {
      explanation: 'Media & Highlights may show photos, videos, or highlights the academy shares with you.',
      used_for: [
        'Viewing media shared by the academy',
        'Seeing highlights connected to you',
      ],
      attention: [
        'Anything you would like to share — speak with staff',
      ],
    },
    Parent: {
      explanation: 'Media & Highlights may show academy media or highlights related to your child when shared.',
      used_for: [
        'Viewing media shared by the academy',
      ],
      attention: [
        'Permission and visibility — speak with staff before re-sharing externally',
      ],
    },
  }),

  g('training', 'Training', {
    Admin: {
      explanation: 'Training helps the academy monitor session activity, attendance, workload, and the consistency of staff data entry.',
      used_for: [
        'Monitoring session activity',
        'Reviewing attendance and workload',
        'Spotting data-entry gaps',
        'Supporting development monitoring',
      ],
      attention: [
        'Attendance updated after each session',
        'Clear session focus',
        'Accurate player participation',
        'Consistency week to week',
      ],
      role_note: 'As Admin, this page is critical for understanding whether other staff are keeping training data current.',
    },
    Staff: {
      explanation: 'Training is used to record sessions, attendance, session focus, intensity, and player participation.',
      used_for: [
        'Recording training sessions',
        'Tracking player attendance',
        'Reviewing session focus and intensity',
        'Supporting workload and development monitoring',
      ],
      attention: [
        'Update attendance after each session',
        'Keep session focus clear',
        'Make sure player participation is accurate',
        'Be consistent week to week',
      ],
      role_note: 'As Staff, please update this page regularly after training so other staff can rely on accurate information.',
    },
    Player: {
      explanation: 'Training helps you follow your training involvement, participation, and development updates shared by the academy.',
      used_for: [
        'Seeing your training history',
        'Following participation updates',
      ],
      attention: [
        'Sessions you missed or attended',
        'Notes the academy adds for you',
      ],
    },
    Parent: {
      explanation: 'Training helps you see training-related information and progress for your child when shared by the academy.',
      used_for: [
        'Following training participation',
        'Seeing updates shared by the academy',
      ],
      attention: [
        'Attendance trends',
        'Notes the academy adds for you',
      ],
    },
  }),

  g('training_programs', 'Training Programs', {
    Admin: {
      explanation: 'Training Programs stores structured training plans, recurring programs, and planned development work.',
      used_for: [
        'Creating training plans',
        'Organising session objectives',
        'Supporting team or individual development',
        'Connecting training work to academy goals',
      ],
      attention: [
        'Program objectives',
        'Correct team / player assignment',
        'Program dates and status',
        'Alignment with player or squad needs',
      ],
    },
    Staff: {
      explanation: 'Training Programs is where you build and manage structured training plans for individuals or squads.',
      used_for: [
        'Creating training plans',
        'Organising session objectives',
        'Assigning programs to teams or players',
      ],
      attention: [
        'Program objectives',
        'Correct assignment',
        'Dates and status',
      ],
    },
    Player: {
      explanation: 'Training Programs may show planned training work shared by the academy.',
      used_for: [
        'Seeing programs shared with you',
      ],
      attention: [
        'Updates from your coach',
      ],
    },
    Parent: {
      explanation: 'Training Programs may show planned training work the academy shares about your child.',
      used_for: [
        'Following planned training work',
      ],
      attention: [
        'Updates from the academy',
      ],
    },
  }),

  g('matches', 'Matches', {
    Admin: {
      explanation: 'Matches stores match schedules, opponents, results, and match-related information.',
      used_for: [
        'Managing upcoming and past matches',
        'Recording opponents and results',
        'Connecting matches to reports',
        'Supporting season tracking',
      ],
      attention: [
        'Correct match dates',
        'Opponent and result accuracy',
        'Missing match reports',
        'Player availability',
      ],
    },
    Staff: {
      explanation: 'Matches is where you manage schedules, opponents, results, and the records that connect to match reports.',
      used_for: [
        'Adding upcoming matches',
        'Recording results',
        'Linking matches to reports',
      ],
      attention: [
        'Correct dates and opponents',
        'Results entered after the game',
        'Missing match reports',
      ],
    },
    Player: {
      explanation: 'Matches helps you see upcoming and past games when the academy shares them.',
      used_for: [
        'Seeing your upcoming matches',
        'Checking past match information',
      ],
      attention: [
        'Match date and venue',
        'Anything the academy asks you to prepare',
      ],
    },
    Parent: {
      explanation: 'Matches helps you follow match information and schedules the academy shares about your child.',
      used_for: [
        'Seeing upcoming matches',
        'Checking past match information',
      ],
      attention: [
        'Match dates and venues',
        'Anything that needs your action',
      ],
    },
  }),

  g('match_reports', 'Match Reports', {
    Admin: {
      explanation: 'Match Reports record player performance, minutes played, feedback, strengths, weaknesses, and development points after games.',
      used_for: [
        'Reviewing player match performance',
        'Recording minutes played and feedback',
        'Supporting PDP and development plans',
        'Tracking performance over time',
      ],
      attention: [
        'Reports completed within 48 hours',
        'Accurate player feedback',
        'Clear development points',
        'Connection to PDP goals',
      ],
    },
    Staff: {
      explanation: 'Match Reports is where you record player performance, minutes played, feedback, and development points after each game.',
      used_for: [
        'Recording match performance',
        'Adding minutes played and feedback',
        'Linking development points to PDP goals',
      ],
      attention: [
        'Complete reports within 48 hours',
        'Be specific in feedback',
        'Link development points to PDP goals',
      ],
    },
    Player: {
      explanation: 'Match Reports help you understand your performance and the areas to improve on.',
      used_for: [
        'Reading staff feedback after matches',
        'Seeing your development points',
      ],
      attention: [
        'Feedback from your coach',
        'Development points to work on',
      ],
    },
    Parent: {
      explanation: 'Match Reports help you see how your child is progressing in matches when shared by staff.',
      used_for: [
        'Reading staff feedback',
        'Seeing development points',
      ],
      attention: [
        'Feedback from the coach',
        'Areas the academy is helping with',
      ],
    },
  }),

  g('fitness_assessments', 'Fitness Assessments', {
    Admin: {
      explanation: 'Fitness Assessments store physical test results and help track player development in areas such as speed, strength, endurance, and mobility.',
      used_for: [
        'Recording physical test results',
        'Comparing progress over time',
        'Supporting performance planning',
        'Identifying physical development needs',
      ],
      attention: [
        'Test dates',
        'Missing results',
        'Large changes in performance',
        'Consistent testing schedule',
      ],
    },
    Staff: {
      explanation: 'Fitness Assessments is where you record physical test results and track development in speed, strength, endurance, and mobility.',
      used_for: [
        'Recording test results',
        'Comparing progress over time',
        'Spotting physical development needs',
      ],
      attention: [
        'Test dates',
        'Missing results',
        'Large changes in performance',
      ],
    },
    Player: {
      explanation: 'Fitness Assessments show your physical testing progress when shared by the academy.',
      used_for: [
        'Seeing your test results',
        'Following your progress over time',
      ],
      attention: [
        'Areas to improve',
        'Notes from staff',
      ],
    },
    Parent: {
      explanation: 'Fitness Assessments help you see your child\'s physical development progress when shared by the academy.',
      used_for: [
        'Following physical development',
      ],
      attention: [
        'Notes from staff',
      ],
    },
  }),

  g('pdp', 'PDP', {
    Admin: {
      explanation: 'PDP (Player Development Plan) is used to set goals, track progress, add feedback, and guide each player\'s individual development.',
      used_for: [
        'Setting player goals',
        'Tracking progress',
        'Adding coach feedback',
        'Connecting training and match performance to development',
      ],
      attention: [
        'Players without PDPs',
        'Goals that need review',
        'Missing feedback',
        'Regular update cadence',
      ],
    },
    Staff: {
      explanation: 'PDP (Player Development Plan) is where you set goals, track progress, and record coach feedback for each player.',
      used_for: [
        'Setting player goals',
        'Tracking progress',
        'Adding coach feedback',
      ],
      attention: [
        'Players without a PDP',
        'Goals that need a review',
        'Missing feedback',
      ],
    },
    Player: {
      explanation: 'PDP shows your development goals and progress.',
      used_for: [
        'Following your goals',
        'Seeing coach feedback',
      ],
      attention: [
        'New goals or feedback',
      ],
    },
    Parent: {
      explanation: 'PDP helps you understand your child\'s development goals and progress over time.',
      used_for: [
        'Following development goals',
        'Reading staff feedback',
      ],
      attention: [
        'New goals or feedback',
      ],
    },
  }),

  g('alerts_flags', 'Alerts & Flags', {
    Admin: {
      explanation: 'Alerts & Flags highlight important issues or concerns that may need staff attention.',
      used_for: [
        'Reviewing player or operational concerns',
        'Tracking important warnings',
        'Supporting early follow-up',
        'Connecting concerns to Work Center tasks',
      ],
      attention: [
        'Open alerts',
        'Repeated issues',
        'High-priority flags',
        'Alerts that need staff follow-up',
      ],
    },
    Staff: {
      explanation: 'Alerts & Flags surface concerns and warnings the system has detected so you can follow up early.',
      used_for: [
        'Reviewing concerns',
        'Tracking warnings',
        'Following up early',
      ],
      attention: [
        'Open alerts',
        'Repeated issues',
        'High-priority flags',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('alert_rules', 'Alert Rules', {
    Admin: {
      explanation: 'Alert Rules control how alerts and flags are triggered by the system.',
      used_for: [
        'Managing alert logic',
        'Setting thresholds',
        'Supporting consistent monitoring',
        'Reducing missed follow-ups',
      ],
      attention: [
        'Rule accuracy',
        'Rules that create too many alerts',
        'Rules that miss important concerns',
        'Changes that affect staff workflows',
      ],
    },
    Staff: {
      explanation: 'Alert Rules control how alerts and flags are triggered. Adjust thresholds carefully — they affect everyone\'s workflow.',
      used_for: [
        'Tuning alert logic',
        'Setting thresholds',
      ],
      attention: [
        'Rules that create too many alerts',
        'Rules that miss important concerns',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('decision_engine', 'Decision Engine', {
    Admin: {
      explanation: 'Decision Engine highlights important academy signals such as missing data, player follow-ups, PDP needs, attendance concerns, and squad-level issues.',
      used_for: [
        'Identifying what needs attention',
        'Reviewing academy signals',
        'Supporting staff decision-making',
        'Understanding priority areas',
      ],
      attention: [
        'Missing-data signals',
        'Player attention areas',
        'Squad-level concerns',
        'When to ask PFAP AI Assistant for a deeper explanation',
      ],
    },
    Staff: {
      explanation: 'Decision Engine surfaces the academy signals you should look at first — missing data, follow-ups, PDP needs, attendance, and squad-level issues.',
      used_for: [
        'Spotting what needs attention',
        'Reviewing signals',
        'Prioritising your work',
      ],
      attention: [
        'Missing-data signals',
        'Player attention areas',
        'When to ask PFAP AI Assistant for a deeper explanation',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('pfap_ai_assistant', 'PFAP AI Assistant', {
    Admin: {
      explanation: 'PFAP AI Assistant helps authorised staff search PFAP Hub data, summarise players or squads, detect missing information, and suggest staff-reviewed actions.',
      used_for: [
        'Asking questions about PFAP Hub data',
        'Summarising players or squads',
        'Finding missing information',
        'Creating staff-reviewed Work Center tasks',
      ],
      attention: [
        'Source labels (Internal / General / Mixed / Not Enough Data)',
        'Safety notes',
        'Missing data',
        'Human review requirements',
      ],
      role_note: 'PFAP AI Assistant does not replace coaches, physios, nutritionists, or academy leadership. AI-created Work Center tasks require human confirmation.',
    },
    Staff: {
      explanation: 'PFAP AI Assistant helps you search PFAP Hub data, summarise players or squads, find missing information, and create staff-reviewed Work Center tasks.',
      used_for: [
        'Searching PFAP Hub data',
        'Summarising players or squads',
        'Finding missing information',
        'Drafting staff-reviewed Work Center tasks',
      ],
      attention: [
        'Source labels',
        'Safety notes',
        'Missing data',
        'Human review requirements',
      ],
      role_note: 'PFAP AI Assistant does not replace coaches, physios, nutritionists, or academy leadership. Every AI-suggested action needs your confirmation.',
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('squad_analytics', 'Squad Analytics', {
    Admin: {
      explanation: 'Squad Analytics provides team-level insights about player groups, squad trends, and collective performance or availability patterns.',
      used_for: [
        'Reviewing squad trends',
        'Comparing team-level data',
        'Understanding group strengths or gaps',
        'Supporting staff planning',
      ],
      attention: [
        'Missing squad data',
        'Attendance or availability trends',
        'Team-level performance patterns',
        'Groups needing follow-up',
      ],
    },
    Staff: {
      explanation: 'Squad Analytics gives you a team-level view of trends, performance patterns, and availability across the squad.',
      used_for: [
        'Reviewing squad trends',
        'Spotting group strengths or gaps',
      ],
      attention: [
        'Missing squad data',
        'Attendance trends',
        'Groups needing follow-up',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('academy_overview', 'Academy Overview', {
    Admin: {
      explanation: 'Academy Overview gives a bigger picture of academy operations, player data, performance, health, development, and administrative status.',
      used_for: [
        'Reviewing academy-wide information',
        'Monitoring high-level progress',
        'Supporting management decisions',
        'Identifying areas needing improvement',
      ],
      attention: [
        'Academy-wide trends',
        'Missing-data patterns',
        'Operational issues',
        'Player or squad risk areas',
      ],
    },
    Staff: {
      explanation: 'Academy Overview gives a high-level picture of academy operations, performance, health, and development.',
      used_for: [
        'Reviewing academy-wide information',
        'Monitoring progress',
      ],
      attention: [
        'Trends across the academy',
        'Missing-data patterns',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('season_reports', 'Season Reports', {
    Admin: {
      explanation: 'Season Reports summarise performance, development, attendance, health, and academy progress across a season.',
      used_for: [
        'Reviewing seasonal progress',
        'Creating management reports',
        'Comparing goals and outcomes',
        'Supporting end-of-season evaluation',
      ],
      attention: [
        'Complete match and training data',
        'PDP progress',
        'Player availability',
        'Missing season records',
      ],
    },
    Staff: {
      explanation: 'Season Reports summarise the season — performance, development, attendance, and player availability.',
      used_for: [
        'Reviewing seasonal progress',
        'Comparing goals and outcomes',
      ],
      attention: [
        'Complete match and training data',
        'Missing season records',
      ],
    },
    Player: {
      explanation: 'Season Reports may show season progress information shared by the academy.',
      used_for: [
        'Following your season progress',
      ],
      attention: [
        'Notes shared by staff',
      ],
    },
    Parent: {
      explanation: 'Season Reports may show season progress information the academy shares about your child.',
      used_for: [
        'Following season progress',
      ],
      attention: [
        'Notes shared by staff',
      ],
    },
  }),

  g('compare_seasons', 'Compare Seasons', {
    Admin: {
      explanation: 'Compare Seasons helps compare academy, squad, or player data across different seasons.',
      used_for: [
        'Understanding progress year to year',
        'Comparing performance trends',
        'Reviewing changes in attendance, development, or results',
        'Supporting strategic planning',
      ],
      attention: [
        'Correct season selection',
        'Data completeness',
        'Meaningful trends',
        'Context behind differences',
      ],
    },
    Staff: {
      explanation: 'Compare Seasons helps you compare data across seasons to spot meaningful trends.',
      used_for: [
        'Year-to-year comparisons',
        'Reviewing changes in performance and attendance',
      ],
      attention: [
        'Correct season selection',
        'Data completeness',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('compare_athletes', 'Compare Athletes', {
    Admin: {
      explanation: 'Compare Athletes helps staff compare selected players across relevant data points.',
      used_for: [
        'Comparing development areas',
        'Reviewing performance or assessment differences',
        'Supporting coaching discussions',
        'Identifying individual strengths and needs',
      ],
      attention: [
        'Fair comparison context',
        'Position and age differences',
        'Missing data',
        'Development-focused interpretation',
      ],
    },
    Staff: {
      explanation: 'Compare Athletes lets you put two or more players side by side across relevant data points.',
      used_for: [
        'Comparing development areas',
        'Reviewing performance differences',
      ],
      attention: [
        'Fair comparison context',
        'Position and age differences',
        'Missing data',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('daily_monitoring', 'Daily Monitoring', {
    Admin: {
      explanation: 'Daily Monitoring tracks regular player wellbeing, readiness, soreness, fatigue, and other daily check-in information.',
      used_for: [
        'Monitoring player readiness',
        'Identifying wellbeing concerns',
        'Supporting training-load decisions',
        'Helping staff follow up earlier',
      ],
      attention: [
        'Missing daily entries',
        'Sudden changes',
        'Repeated soreness or fatigue',
        'Players needing staff follow-up',
      ],
    },
    Staff: {
      explanation: 'Daily Monitoring helps you track player wellbeing, readiness, soreness, and fatigue based on daily check-ins.',
      used_for: [
        'Monitoring player readiness',
        'Spotting wellbeing concerns',
        'Supporting training-load decisions',
      ],
      attention: [
        'Missing daily entries',
        'Sudden changes',
        'Repeated soreness or fatigue',
      ],
    },
    Player: {
      explanation: 'Daily Monitoring is where you share how you are feeling so the academy can support training and recovery.',
      used_for: [
        'Sharing how you feel today',
        'Helping staff support training and recovery',
      ],
      attention: [
        'Be honest with sleep, soreness, and fatigue',
        'Update daily when asked',
      ],
    },
    Parent: {
      explanation: 'Daily Monitoring may show wellbeing updates the academy shares about your child.',
      used_for: [
        'Following wellbeing updates',
      ],
      attention: [
        'Anything the academy asks you to follow up on',
      ],
    },
  }),

  g('injury_recovery', 'Injury & Recovery', {
    Admin: {
      explanation: 'Injury & Recovery tracks injuries, recovery status, restrictions, target return dates, and physio updates.',
      used_for: [
        'Recording injury cases',
        'Tracking recovery status',
        'Managing restrictions and target dates',
        'Supporting safe staff communication',
      ],
      attention: [
        'Active injuries without recovery plans',
        'Missing target return dates',
        'Updated physio notes',
        'Restrictions before training decisions',
      ],
      role_note: 'Medical decisions are always handled by qualified staff (physio / medical team). PFAP Hub records information; it does not authorise return-to-play.',
    },
    Staff: {
      explanation: 'Injury & Recovery tracks injuries, recovery status, restrictions, and physio updates so the right staff can follow up safely.',
      used_for: [
        'Recording injury cases',
        'Tracking recovery status',
        'Reading restrictions before training',
      ],
      attention: [
        'Active injuries without a recovery plan',
        'Missing return dates',
        'Restrictions before training decisions',
      ],
      role_note: 'Medical decisions must be handled by qualified physio / medical staff. Use this page to record information, not to clear players.',
    },
    Player: {
      explanation: 'This section may show recovery-related updates the academy shares with you. Return-to-play decisions are handled by qualified staff.',
      used_for: [
        'Reading recovery updates',
        'Following restrictions shared by the academy',
      ],
      attention: [
        'Restrictions and notes from staff',
      ],
      role_note: 'Always follow your physio / medical team. PFAP Hub does not clear players to return to training or play.',
    },
    Parent: {
      explanation: 'This section may show recovery-related updates about your child. Return-to-play decisions are handled by qualified medical staff.',
      used_for: [
        'Following recovery updates',
      ],
      attention: [
        'Restrictions and notes from staff',
      ],
      role_note: 'Always defer to the academy\'s physio / medical team for return-to-play decisions.',
    },
  }),

  g('biometrics', 'Biometrics', {
    Admin: {
      explanation: 'Biometrics stores body and physical-profile data such as weight, height, body composition, and full body-circumference measurements (Shoulder, Upper Arm L/R, Forearm L/R, Thigh L/R, Calf L/R) used by authorised staff. Phase 84.1 introduced a lateralised circumference profile and a shared module adapter so any new biometrics field flows automatically into Trial Reports, Season Reports and Athlete 360.',
      used_for: [
        'Tracking physical-profile changes side-by-side (left vs right)',
        'Supporting performance and wellbeing monitoring',
        'Informing staff review + injury risk screening',
        'Connecting data to fitness and development work',
        'Feeding Trial/Season Report snapshots via the shared adapter',
      ],
      attention: [
        'Measurement dates (future dates blocked; back-dating supported)',
        'Missing updates',
        'Sudden changes',
        'Need for qualified staff interpretation',
      ],
    },
    Staff: {
      explanation: 'Biometrics stores body and physical-profile data — weight, height, body composition, and similar measurements.',
      used_for: [
        'Tracking physical-profile changes',
        'Supporting performance and wellbeing monitoring',
      ],
      attention: [
        'Measurement dates',
        'Missing updates',
        'Sudden changes — review with qualified staff',
      ],
    },
    Player: {
      explanation: 'Biometrics may show physical-profile information recorded by the academy.',
      used_for: [
        'Seeing your measurements over time',
      ],
      attention: [
        'Speak with staff if anything looks wrong',
      ],
    },
    Parent: {
      explanation: 'Biometrics may show physical-profile updates the academy shares about your child.',
      used_for: [
        'Following physical-profile updates',
      ],
      attention: [
        'Anything that needs follow-up — speak with staff',
      ],
    },
  }),

  g('wellness_library', 'Wellness Library', {
    Admin: {
      explanation: 'Wellness Library stores guidance, resources, and information related to player wellbeing, habits, recovery, and academy support.',
      used_for: [
        'Sharing wellbeing resources',
        'Supporting healthy habits',
        'Organising wellness information',
        'Helping staff guide players appropriately',
      ],
      attention: [
        'Resource relevance',
        'Updated content',
        'Age-appropriate guidance',
        'When qualified staff review is needed',
      ],
    },
    Staff: {
      explanation: 'Wellness Library is where the academy keeps wellbeing resources, habit guides, and recovery information.',
      used_for: [
        'Sharing wellbeing resources',
        'Supporting healthy habits',
      ],
      attention: [
        'Resource relevance',
        'Age-appropriate guidance',
      ],
    },
    Player: {
      explanation: 'Wellness Library has helpful resources about wellbeing, habits, and recovery.',
      used_for: [
        'Reading wellbeing resources',
        'Learning healthy habits',
      ],
      attention: [
        'Pick what is relevant to you',
        'Ask staff if you have questions',
      ],
    },
    Parent: {
      explanation: 'Wellness Library has academy-approved wellbeing information you can read.',
      used_for: [
        'Reading wellbeing resources shared by the academy',
      ],
      attention: [
        'Ask staff if you have questions',
      ],
    },
  }),

  g('mental_hub', 'Mental Hub', {
    Admin: {
      explanation: 'Mental Hub supports mental wellbeing, mindset, motivation, confidence, and staff awareness around player support. Phase 84.2 introduced subsection permissions — each of the three workspaces (Athlete Space, Parent Insights, Mentorship) has independent View/Create/Edit/Delete controls in Access Control.',
      used_for: [
        'Recording or reviewing mental-support information when permitted',
        'Supporting player wellbeing',
        'Identifying players needing support',
        'Keeping sensitive notes protected',
        'Granting specialised Mental Coaches access to only the workspaces they need (e.g. Mentorship-only for pairing management)',
      ],
      attention: [
        'Privacy and sensitivity',
        'Role permissions (per-subsection since Phase 84.2)',
        'Tabs auto-hide when a coach has no View permission on that subsection',
        'Players needing support',
        'Follow-up by appropriate staff',
      ],
    },
    Staff: {
      explanation: 'Mental Hub supports mental wellbeing, mindset, and player-support work. Notes here can be sensitive — be careful with permissions.',
      used_for: [
        'Recording or reviewing wellbeing information when permitted',
        'Identifying players who may need support',
      ],
      attention: [
        'Privacy and sensitivity',
        'Role permissions before sharing',
        'Follow-up by appropriate staff',
      ],
    },
    Player: {
      explanation: 'This area supports mindset and wellbeing.',
      used_for: [
        'Resources or notes shared with you',
      ],
      attention: [
        'Speak with staff if you need support',
      ],
    },
    Parent: {
      explanation: 'The academy may use this area to support player wellbeing.',
      used_for: [
        'Information shared with you by the academy',
      ],
      attention: [
        'Speak with staff if you have concerns',
      ],
    },
  }),

  g('academic', 'Academic', {
    Admin: {
      explanation: 'Academic tracks school and education-related progress, updates, and support needs.',
      used_for: [
        'Reviewing academic progress',
        'Tracking education updates',
        'Supporting school-football balance',
        'Identifying players needing academic support',
      ],
      attention: [
        'Missing academic records',
        'Term updates',
        'Support needs',
        'Balance between football and education',
      ],
    },
    Staff: {
      explanation: 'Academic tracks school and education progress so the academy can support school-football balance.',
      used_for: [
        'Reviewing academic progress',
        'Tracking education updates',
        'Spotting players needing academic support',
      ],
      attention: [
        'Missing academic records',
        'Term updates',
        'Support needs',
      ],
    },
    Player: {
      explanation: 'Academic shows education-related information and progress when shared by the academy.',
      used_for: [
        'Following your education updates',
      ],
      attention: [
        'Notes from staff',
      ],
    },
    Parent: {
      explanation: 'Academic helps you follow education-related updates the academy shares about your child.',
      used_for: [
        'Following education updates',
      ],
      attention: [
        'Notes from staff',
      ],
    },
  }),

  g('career_pathway', 'Career Pathway', {
    Admin: {
      explanation: 'Career Pathway helps track long-term player goals, football opportunities, education direction, and future planning.',
      used_for: [
        'Recording career goals',
        'Supporting pathway discussions',
        'Tracking opportunities and next steps',
        'Connecting development to future plans',
      ],
      attention: [
        'Missing pathway records',
        'Player goals',
        'Review dates',
        'Realistic next steps',
      ],
    },
    Staff: {
      explanation: 'Career Pathway is where you record long-term goals, opportunities, and future planning for each player.',
      used_for: [
        'Recording career goals',
        'Tracking opportunities and next steps',
      ],
      attention: [
        'Missing pathway records',
        'Realistic next steps',
        'Review dates',
      ],
    },
    Player: {
      explanation: 'Career Pathway shows your future goals and planning.',
      used_for: [
        'Following your goals',
        'Reading pathway notes',
      ],
      attention: [
        'New goals or notes from staff',
      ],
    },
    Parent: {
      explanation: 'Career Pathway helps the academy support long-term planning for your child.',
      used_for: [
        'Following long-term goals and plans',
      ],
      attention: [
        'Notes shared by staff',
      ],
    },
  }),

  g('finance', 'Finance', {
    Admin: {
      explanation: 'Finance helps manage payments, fees, balances, and finance-related records where permitted.',
      used_for: [
        'Tracking payments',
        'Reviewing balances',
        'Supporting financial administration',
        'Keeping finance records organised',
      ],
      attention: [
        'Missing or overdue payments',
        'Incorrect records',
        'Permission-sensitive information',
        'Follow-up needs',
      ],
    },
    Staff: {
      explanation: 'Finance helps you track payments, fees, and balances where you have permission to view them.',
      used_for: [
        'Tracking payments',
        'Reviewing balances',
      ],
      attention: [
        'Missing or overdue payments',
        'Permission-sensitive information',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: {
      explanation: 'Finance may show payment-related information or records the academy shares with you.',
      used_for: [
        'Reviewing payment information shared with you',
      ],
      attention: [
        'Anything that needs your action',
      ],
    },
  }),

  g('documents', 'Documents', {
    Admin: {
      explanation: 'Documents stores important academy files, player documents, forms, and records.',
      used_for: [
        'Managing player documents',
        'Storing forms and records',
        'Supporting compliance and administration',
        'Sharing approved files',
      ],
      attention: [
        'Missing required documents',
        'Outdated files',
        'Correct player association',
        'Permission and visibility',
      ],
    },
    Staff: {
      explanation: 'Documents is where the academy keeps important files, forms, and records linked to players or operations.',
      used_for: [
        'Managing player documents',
        'Storing forms and records',
      ],
      attention: [
        'Missing required documents',
        'Outdated files',
        'Correct player association',
      ],
    },
    Player: {
      explanation: 'Documents may show important files or forms the academy shares with you.',
      used_for: [
        'Reading shared files',
        'Finding forms shared with you',
      ],
      attention: [
        'Anything the academy asks you to read or sign',
      ],
    },
    Parent: {
      explanation: 'Documents may show important files, forms, or records the academy shares with you.',
      used_for: [
        'Reading shared files',
        'Finding forms shared with you',
      ],
      attention: [
        'Anything the academy asks you to read or sign',
      ],
    },
  }),

  g('communication', 'Communication', {
    Admin: {
      explanation: 'Communication helps staff manage messages, updates, and follow-ups with players, parents, and academy members.',
      used_for: [
        'Recording communication',
        'Sending or reviewing updates',
        'Tracking follow-ups',
        'Keeping staff aligned',
      ],
      attention: [
        'Missed follow-ups',
        'Important parent / player updates',
        'Clear communication history',
        'Sensitive information',
      ],
    },
    Staff: {
      explanation: 'Communication is where you manage messages and follow-ups with players, parents, and other staff.',
      used_for: [
        'Sending and reviewing updates',
        'Tracking follow-ups',
      ],
      attention: [
        'Missed follow-ups',
        'Important parent / player updates',
        'Sensitive information',
      ],
    },
    Player: {
      explanation: 'Communication shows important messages and updates from the academy.',
      used_for: [
        'Reading academy messages',
      ],
      attention: [
        'New messages',
        'Anything that needs your reply',
      ],
    },
    Parent: {
      explanation: 'Communication helps you receive important academy messages and updates about your child.',
      used_for: [
        'Reading academy messages',
        'Replying to staff updates',
      ],
      attention: [
        'New messages',
        'Anything that needs your reply',
      ],
    },
  }),

  g('seasons', 'Seasons', {
    Admin: {
      explanation: 'Seasons manages academy seasons, date ranges, and season-related settings or records.',
      used_for: [
        'Organising data by season',
        'Supporting reports',
        'Comparing season performance',
        'Managing current and past periods',
      ],
      attention: [
        'Correct active season',
        'Season dates',
        'Data linked to the right season',
        'Archived seasons',
      ],
    },
    Staff: {
      explanation: 'Seasons manages academy season periods so reports and data are linked to the right time range.',
      used_for: [
        'Selecting the active season',
        'Linking data to a season',
      ],
      attention: [
        'Correct active season',
        'Season dates',
      ],
    },
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('user_management', 'User Management', {
    Admin: {
      explanation: 'User Management is used to manage platform users, roles, and account access.',
      used_for: [
        'Creating or reviewing users',
        'Managing roles',
        'Supporting account access',
        'Keeping user records accurate',
      ],
      attention: [
        'Correct role assignment',
        'Inactive users',
        'Duplicate accounts',
        'Access security',
      ],
    },
    Staff: ROLE_HIDDEN,
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('access_control', 'Access Control', {
    Admin: {
      explanation: 'Access Control manages what each role or user can see and do inside PFAP Hub. Module permissions decide which pages and actions are available; Work Center task visibility for Staff is controlled separately via the Work Areas assigned in User Management.',
      used_for: [
        'Managing permissions',
        'Protecting sensitive data',
        'Controlling feature access',
        'Supporting safe role-based workflows',
      ],
      attention: [
        'Over-permissioned users',
        'Sensitive module access',
        'Role-specific restrictions',
        'Changes that affect staff workflow — for Work Center task visibility, edit the user\'s Work Areas in User Management instead',
      ],
    },
    Staff: ROLE_HIDDEN,
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('manage_accounts', 'Manage Accounts', {
    Admin: {
      explanation: 'Manage Accounts supports account maintenance, user details, and access-related administration.',
      used_for: [
        'Reviewing account information',
        'Updating account-related settings',
        'Supporting login / access issues',
        'Keeping account records organised',
      ],
      attention: [
        'Account status',
        'Role consistency',
        'Missing user details',
        'Access issues',
      ],
    },
    Staff: ROLE_HIDDEN,
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('bulk_import', 'Bulk Import', {
    Admin: {
      explanation: 'Bulk Import helps add or update many records at once, such as users, athletes, or other structured data.',
      used_for: [
        'Importing many records faster',
        'Reducing manual entry',
        'Supporting data setup',
        'Updating structured records',
      ],
      attention: [
        'File format',
        'Duplicate records',
        'Data accuracy before import',
        'Import errors',
      ],
    },
    Staff: ROLE_HIDDEN,
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('staff_credentials', 'Staff Credentials', {
    Admin: {
      explanation: 'Staff Credentials helps manage staff login / access details and staff account information.',
      used_for: [
        'Managing staff access details',
        'Supporting onboarding',
        'Reviewing credential-related records',
        'Keeping staff accounts organised',
      ],
      attention: [
        'Security',
        'Correct staff assignment',
        'Outdated credentials',
        'Access-control rules',
      ],
    },
    Staff: ROLE_HIDDEN,
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),

  g('system_logs', 'System Logs', {
    Admin: {
      explanation: 'System Logs show important platform activity, system events, and audit information.',
      used_for: [
        'Reviewing system activity',
        'Checking audit trails',
        'Investigating issues',
        'Supporting security and accountability',
      ],
      attention: [
        'Unusual activity',
        'Failed actions',
        'Permission changes',
        'Important system events',
      ],
    },
    Staff: ROLE_HIDDEN,
    Player: ROLE_HIDDEN,
    Parent: ROLE_HIDDEN,
  }),
];

// Index by id for quick lookups.
export const GUIDE_BY_ID = GUIDE_REGISTRY.reduce((acc, entry) => {
  acc[entry.id] = entry;
  return acc;
}, {});

/**
 * Map URL pathname → guide_id. The mapping prefers exact matches but
 * falls back to a startsWith() check for parameterised routes (e.g.
 * `/athletes/:id/...` is still treated as the Athletes guide).
 *
 * Pages NOT in this map deliberately have no guide overlay (e.g. nested
 * sub-routes, edit pages, role-specific dashboards we did not list).
 */
const PATH_TO_GUIDE = [
  ['/dashboard', 'dashboard'],
  ['/parent-dashboard', 'dashboard'],
  ['/work-center', 'work_center'],
  ['/focus-legacy', 'work_center'],
  ['/focus-v2', 'work_center'],
  ['/focus-hub', 'work_center'],
  ['/focus', 'work_center'],
  ['/athletes/compare', 'compare_athletes'],
  ['/athletes', 'athletes'],
  ['/my-athletes', 'athletes'],
  ['/my-profile', 'athletes'],
  ['/athlete-tags', 'athlete_tags'],
  ['/team-state', 'team_state'],
  ['/media', 'media_highlights'],
  ['/my-media', 'media_highlights'],
  ['/training-programs', 'training_programs'],
  ['/performance-programs', 'training_programs'],
  ['/training', 'training'],
  ['/my-training', 'training'],
  ['/match-reports', 'match_reports'],
  ['/matches', 'matches'],
  ['/my-performance', 'matches'],
  ['/assessments', 'fitness_assessments'],
  ['/my-assessments', 'fitness_assessments'],
  ['/pdp', 'pdp'],
  ['/alerts-flags', 'alerts_flags'],
  ['/alert-config', 'alert_rules'],
  ['/intelligence/ai-assistant', 'pfap_ai_assistant'],
  ['/intelligence', 'decision_engine'],
  ['/analytics/squad', 'squad_analytics'],
  ['/analytics/academy', 'academy_overview'],
  ['/season-reports/compare', 'compare_seasons'],
  ['/season-reports', 'season_reports'],
  ['/wellness-monitoring', 'daily_monitoring'],
  ['/my-wellness', 'daily_monitoring'],
  ['/injury-recovery', 'injury_recovery'],
  ['/medical', 'injury_recovery'],
  ['/my-medical', 'injury_recovery'],
  ['/biometrics', 'biometrics'],
  ['/wellness', 'wellness_library'],
  ['/mental-hub', 'mental_hub'],
  ['/academic', 'academic'],
  ['/my-academic', 'academic'],
  ['/career-pathway', 'career_pathway'],
  ['/finance/reports', 'finance'],
  ['/finance', 'finance'],
  ['/my-finance/reports', 'finance'],
  ['/my-finance', 'finance'],
  ['/documents', 'documents'],
  ['/my-documents', 'documents'],
  ['/announcements', 'communication'],
  ['/my-announcements', 'communication'],
  ['/seasons', 'seasons'],
  ['/admin/users', 'user_management'],
  ['/admin/access-control', 'access_control'],
  ['/admin/manage-accounts', 'manage_accounts'],
  ['/admin/bulk-import', 'bulk_import'],
  ['/admin/staff-credentials', 'staff_credentials'],
  ['/system-logs', 'system_logs'],
];

export function guideIdForPath(pathname) {
  if (!pathname) return null;
  // Longest-prefix-match wins (so /athletes/compare is checked before /athletes).
  const sorted = [...PATH_TO_GUIDE].sort((a, b) => b[0].length - a[0].length);
  for (const [prefix, gid] of sorted) {
    if (pathname === prefix || pathname.startsWith(prefix + '/') || pathname.startsWith(prefix + '?')) {
      return gid;
    }
  }
  return null;
}
